import CloseIcon from '@mui/icons-material/Close'
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation'
import InventoryIcon from '@mui/icons-material/Inventory'
import SearchIcon from '@mui/icons-material/Search'

import LocalStorageButtons from '@components/LocalStorageButtons'
import { FilterContactpersoon } from '@components/toolbar/FilterContactpersoon'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterFromToWeek from '@components/toolbar/FilterFromToWeek'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import FilterKpi from '@components/toolbar/FilterKpi'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterSelectString } from '@components/toolbar/FilterSelectString'
import { FilterToggleButton } from '@components/toolbar/FilterToggleButton'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import { activiteittypesOptions } from '@util/hoofdleiding-util'
import { filterVerantwoordelijkeOptionsByOrganisatie } from '@util/project-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import type { ProcesstapHoofdleiding } from '../../../status/types/ProcesstapHoofdleiding'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { queryKey } from '../hooks/useActiviteitenSearch'
import type { ActiviteitenSearchFilters as Filters } from '../types/ActiviteitenSearch'

const kpiTooltips = {
  red: 'Laat alleen de verlopen activiteiten zien',
  amber: 'Laat bijna verlopen activiteiten zien',
  green: 'Laat alle activiteiten zien die niet (bijna) verlopen zijn',
  grey: 'Laat alle activiteiten zien',
}

interface Props {
  onChange: (filters: Filters) => void
  filters: Filters
  initialFilters: Filters
  onRefresh: () => void
  onReset: () => void
  filterOptions: {
    coordinerenden: string[]
    hoofdleiding: {
      gebieden: FilterOption[]
      netbeheerders: string[]
      gemeenten: string[]
      verantwoordelijken: GroupedFilterOption[]
      typeWerkTypes: string[]
    }
    labels: FilterOption[]
    processtappen: ProcesstapHoofdleiding[]
  }
}

export const ActiviteitenFilters = ({
  onChange,
  filters,
  initialFilters,
  onRefresh,
  onReset,
  filterOptions: {
    coordinerenden,
    hoofdleiding: {
      gebieden,
      netbeheerders,
      gemeenten,
      verantwoordelijken,
      typeWerkTypes,
    },
    labels: labelOptions,
    processtappen,
  },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    gebied,
    type,
    assignedTo,
    fromDate,
    toDate,
    fromChangedDate,
    toChangedDate,
    labels,
    myList,
    startUitvoeringWeekVan,
    startUitvoeringWeekTot,
    gemeente,
    verantwoordelijke,
    contactpersoon,
    typeWerk,
    due,
    processtatus,
    kpi,
  } = localFilters
  const { combi, organisatie } = useAuthenticatedUser()

  const buildOption = (group) => (label) => ({ group, label })
  const assignees = (combi ? [buildOption('Combi')(combi)] : [])
    .concat(netbeheerders.map(buildOption('Netbeheerder')))
    .concat(coordinerenden.map(buildOption('Coordinerende')))

  const verantwoordelijkeOptions = filterVerantwoordelijkeOptionsByOrganisatie(
    verantwoordelijken,
    organisatie,
  )

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectObject
          multiple
          options={activiteittypesOptions}
          label="Type activiteit"
          filterKey="type"
          value={type}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={gebieden}
          label="Gebied"
          filterKey="gebied"
          value={gebied}
          onFilterChange={handleFilterChange}
          multiple
        />
      </FilterGridItem>

      {combi && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={assignees}
            label="Toegewezen aan"
            filterKey="assignedTo"
            value={assignedTo}
            onFilterChange={handleFilterChange}
            groupByProp="group"
          />
        </FilterGridItem>
      )}
      <FilterGridItem>
        <FilterSelectObject
          options={verantwoordelijkeOptions}
          label="Projectverantwoordelijke"
          filterKey="verantwoordelijke"
          value={verantwoordelijke}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterContactpersoon
          value={contactpersoon}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {labelOptions.length > 0 && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={labelOptions}
            label="Labels"
            filterKey="labels"
            value={labels}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          onFilterChange={handleFilterChange}
          label="Opleverdatum"
          fromDate={fromDate}
          toDate={toDate}
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          onFilterChange={handleFilterChange}
          label="Wijzigingsdatum"
          fromDate={fromChangedDate}
          toDate={toChangedDate}
          prop="ChangedDate"
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToWeek
          fromWeek={startUitvoeringWeekVan}
          toWeek={startUitvoeringWeekTot}
          onFilterChange={handleFilterChange}
          label="Project gepland"
          fromKey="startUitvoeringWeekVan"
          toKey="startUitvoeringWeekTot"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={gemeente}
          options={gemeenten}
          label="Gemeente"
          filterKey="gemeente"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={typeWerk}
          options={typeWerkTypes}
          label="Type werk"
          filterKey="typeWerk"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={processtatus}
          options={processtappen}
          label="Processtap"
          filterKey="processtatus"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <FilterKpi
          onFilterChange={handleFilterChange}
          kpi={kpi}
          tooltips={kpiTooltips}
        />
        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Zoeken"
          onClick={handleZoeken}
        />

        <FilterToggleButton
          filterKey="due"
          value={due}
          onFilterChange={handleFilterChange}
          activeIcon={<InventoryIcon />}
          inactiveIcon={<InsertInvitationIcon />}
          activeTitle="Toon alles"
          inactiveTitle="Toon alleen gepland"
        />

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
